import _defineProperty from '@babel/runtime/helpers/defineProperty';
import _extends from '@babel/runtime/helpers/extends';
import React__default, { useContext } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { isNumber } from '../../utils/objects.js';
import useWarning from '../../hooks/useWarning.js';
import Alerts from './Alerts.js';

const AlertsContext = /*#__PURE__*/React__default.createContext(undefined);
const useAlerts = () => {
  const value = useContext(AlertsContext);
  process.env.NODE_ENV !== "production" ? useWarning(!value, 'useAlerts hook must be used inside AlertsProvider') : void 0;
  return value;
};
const withAlerts = Comp => props => /*#__PURE__*/React__default.createElement(AlertsContext.Consumer, null, providerProps => /*#__PURE__*/React__default.createElement(Comp, _extends({}, props, {
  alerts: providerProps
})));
class AlertsProvider extends React__default.Component {
  constructor(props) {
    super(props);
    _defineProperty(this, "value", void 0);
    _defineProperty(this, "toastId", 0);
    _defineProperty(this, "show", options => {
      const {
        maxAlerts,
        timeout
      } = this.props;
      const hasMax = isNumber(maxAlerts) && maxAlerts > 0;
      const {
        timeout: timeoutOption,
        ...otherOptions
      } = options;
      const shouldUseTimeoutOption = isNumber(timeoutOption);
      const id = this.toastId;
      this.toastId += 1;
      this.setState(({
        alerts
      }) => ({
        alerts: [{
          timeout: shouldUseTimeoutOption ? timeoutOption : timeout,
          ...otherOptions,
          id
        }, ...(hasMax && alerts.length >= maxAlerts ? alerts.slice(0, maxAlerts - 1) : alerts)]
      }));
    });
    _defineProperty(this, "dismiss", (id, didTimeoutExpire = false) => {
      this.setState(({
        alerts
      }) => ({
        alerts: alerts.filter(alert => {
          if (alert.id !== id) return true;
          alert.onDismiss?.(didTimeoutExpire);
          return false;
        })
      }));
    });
    _defineProperty(this, "clear", () => {
      this.state.alerts.forEach(alert => alert.onDismiss?.(false));
      this.setState({
        alerts: []
      });
    });
    _defineProperty(this, "success", (title, options = {}) => {
      this.show({
        ...options,
        title,
        intent: 'success'
      });
    });
    _defineProperty(this, "error", (title, options = {}) => {
      this.show({
        ...options,
        title,
        intent: 'danger'
      });
    });
    _defineProperty(this, "warning", (title, options = {}) => {
      this.show({
        ...options,
        title,
        intent: 'warning'
      });
    });
    _defineProperty(this, "info", (title, options = {}) => {
      this.show({
        ...options,
        title,
        intent: 'neutral'
      });
    });
    _defineProperty(this, "getDismissHandler", alert => didTimeoutExpire => {
      this.dismiss(alert.id, didTimeoutExpire);
    });
    this.state = {
      alerts: []
    };
    this.value = {
      success: this.success,
      error: this.error,
      warning: this.warning,
      info: this.info,
      clear: this.clear
    };
  }
  componentDidMount() {
    const containerElement = document.createElement('div');
    document.body.appendChild(containerElement);
    this.setState({
      containerElement
    });
  }
  componentWillUnmount() {
    if (this.state.containerElement) {
      document.body.removeChild(this.state.containerElement);
    }
  }
  render() {
    return /*#__PURE__*/React__default.createElement(AlertsContext.Provider, {
      value: this.value
    }, this.state.containerElement && /*#__PURE__*/ReactDOM.createPortal( /*#__PURE__*/React__default.createElement(Alerts, {
      alerts: this.state.alerts,
      getDismissHandler: this.getDismissHandler,
      onClear: this.clear
    }), this.state.containerElement), this.props.children);
  }
}
_defineProperty(AlertsProvider, "displayName", 'NuiAlertsProvider');
_defineProperty(AlertsProvider, "propTypes", {
  /**
   * maximum number of alerts allowed to appear simultaneously (oldest removed first)
   */
  maxAlerts: PropTypes.number,
  /**
   * time in ms before alerts automatically disappear
   */
  timeout: PropTypes.number,
  children: PropTypes.node.isRequired
});
_defineProperty(AlertsProvider, "defaultProps", {
  maxAlerts: 10,
  timeout: 5000
});
var AlertsProvider$1 = AlertsProvider;

export { AlertsProvider$1 as default, useAlerts, withAlerts };
